import { LocaleEnum } from "lopermut"
import { z } from "zod"
import { Errors } from "../errors"
import {
  CountryFilterSchema,
  createFilterBySchema,
  oneOrMany,
  validatePhoneNumber,
} from "../shared/filter_dtos"
import { PaginationSchema } from "../shared/pagination_dtos"
import { QuerySchema } from "../shared/query_dtos"
import { createSortSchema } from "../shared/sort_dtos"

export const MarketingChannelEnum = {
  Email: "email",
  Sms: "sms",
  Whatsapp: "whatsapp",
} as const

export type MarketingChannel = (typeof MarketingChannelEnum)[keyof typeof MarketingChannelEnum]

export const customersSortFields = [
  "id",
  "name",
  "sentiment",
  "phone",
  "email",
  "locale",
  "lastMessageSentAt",
  "messagesCount",
  "createdAt",
  "instagramProfile",
] as const
export type CustomersSortFields = (typeof customersSortFields)[number]
export const CustomersSortSchema = createSortSchema(
  customersSortFields,
  "lastMessageSentAt",
  "desc",
)

export const ToggleCustomerAISchema = z.object({
  aiDisabled: z.boolean(),
})

export const MAX_TAG_NAME_LENGTH = 255

// Commas are reserved: tags travel comma separated in imports and in the Shopify API
export const TagNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_TAG_NAME_LENGTH)
  .regex(/^[^,]+$/)

export const CreateTagsSchema = z.object({
  names: z.array(TagNameSchema).min(1),
})

export const UpdateCustomerTagsSchema = z.object({
  add: z.array(TagNameSchema).default([]),
  remove: z.array(TagNameSchema).default([]),
})

export const CustomersFilterBySchema = createFilterBySchema([
  {
    key: "name",
    value: z.string().optional(),
  },
  {
    key: "country",
    value: oneOrMany(z.string()),
  },
  {
    key: "locale",
    value: oneOrMany(LocaleEnum),
  },
  {
    key: "campaignId",
    value: oneOrMany(z.string()),
  },
  {
    key: "hasResponses",
    value: z
      .union([z.boolean(), z.string()])
      .optional()
      .transform((value) => {
        if (typeof value === "string") return value === "true"
        return value
      }),
  },
  {
    key: "isFake",
    value: z
      .union([z.boolean(), z.literal("true"), z.literal("false")])
      .optional()
      .transform((value) => {
        if (typeof value === "string") return value === "true"
        return value
      }),
  },
  {
    key: "segmentIds",
    value: oneOrMany(z.string()),
  },
  {
    key: "tagIds",
    value: oneOrMany(z.string().regex(/^\d+$/)),
  },
  {
    key: "subscribedTo",
    value: oneOrMany(z.enum(MarketingChannelEnum)),
  },
  {
    key: "instagramProfile",
    value: z.string().optional(),
  },
  {
    key: "hasOptedOut",
    value: z
      .union([z.boolean(), z.literal("true"), z.literal("false")])
      .optional()
      .transform((value) => {
        if (typeof value === "string") return value === "true"
        return value
      }),
  },
  {
    key: "hasEmail",
    value: z
      .union([z.boolean(), z.literal("true"), z.literal("false")])
      .optional()
      .transform((value) => {
        if (typeof value === "string") return value === "true"
        return value
      }),
  },
  {
    key: "hasPhone",
    value: z
      .union([z.boolean(), z.literal("true"), z.literal("false")])
      .optional()
      .transform((value) => {
        if (typeof value === "string") return value === "true"
        return value
      }),
  },
] as const)

export const GetCustomersSchema = z.object({
  ...PaginationSchema.extend(QuerySchema.shape)
    .extend(CustomersSortSchema.shape)
    .extend(CustomersFilterBySchema.shape).shape,
})

export const GetCustomerSchema = z.object({
  id: z
    .string()
    .refine((val) => /^\d+$/.test(val), { message: Errors.INVALID_NUMBER })
    .transform(BigInt),
})

export const GetCustomerLogsSchema = PaginationSchema

export const GenerateFakeCustomerSchema = z.object({
  campaignId: z
    .string()
    .refine((val) => /^\d+$/.test(val), { message: Errors.INVALID_NUMBER })
    .transform((val) => BigInt(val)),
})

const CompanyClientSchema = z.object({
  externalId: z.string(),
  name: z.string().optional(),
})

export const BaseCustomerSchema = z.object({
  name: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().nullish(),
  country: CountryFilterSchema,
  locale: LocaleEnum.nullish(),
  useChannel: z.boolean().optional(),
  phone: z.string().nullish(),
  metadata: z.union([z.record(z.string(), z.any()), z.number(), z.boolean(), z.string()]).nullish(),
  client: CompanyClientSchema.nullish(),
})

export const UpsertCustomerBodySchema = BaseCustomerSchema.extend({
  clientId: z
    .string()
    .refine((val) => /^\d+$/.test(val), { message: Errors.INVALID_NUMBER })
    .transform(BigInt)
    .nullish(),
})
  .refine(
    ({ phone, country, locale }) =>
      validatePhoneNumber(
        phone ?? null,
        (country as string | null | undefined) ?? null,
        (locale as string | null | undefined) ?? null,
      ),
    { message: Errors.INVALID_PHONE_NUMBER, path: ["phone"] },
  )
  .refine(
    ({ metadata }) => {
      if (!metadata) return true
      if (typeof metadata === "object") {
        return true
      }
      try {
        JSON.parse(String(metadata))
        return true
      } catch (error) {
        return false
      }
    },
    { message: Errors.CUSTOMER_METADATA_INVALID, path: ["metadata"] },
  )
  .transform(({ metadata, ...rest }) => {
    if (!metadata) return { ...rest, metadata: null }
    if (typeof metadata === "object") return { ...rest, metadata }
    return { ...rest, metadata: JSON.parse(String(metadata)) }
  })

export const UpdateCustomerBodySchema = UpsertCustomerBodySchema.and(
  z.object({ tags: UpdateCustomerTagsSchema.optional() }),
)

const BaseCustomerTriggerSchema = BaseCustomerSchema.extend({
  name: z.string({ message: Errors.CUSTOMER_NAME_INVALID }).nullish(),
  locale: z.string().optional(),
  tags: z.array(TagNameSchema).optional(),
}).omit({
  useChannel: true,
})

// Schema where phone is required, email is optional
const PublicPhoneCustomerSchema = BaseCustomerTriggerSchema.extend({
  referenceId: z.string().optional(),
  phone: z.string(),
  email: z
    .email()
    .transform((val) => (val === "" ? null : val))
    .nullable()
    .refine((val) => val === null || z.email().safeParse(val).success, {
      message: Errors.EMAIL_INVALID,
    })
    .optional(),
  shopifyCustomerId: z.string().nullish(),
}).refine(({ phone, country, locale }) => validatePhoneNumber(phone, country, locale), {
  message: Errors.INVALID_PHONE_NUMBER,
  path: ["phone"],
})

// Schema where email is required, phone is optional
const PublicEmailCustomerSchema = BaseCustomerTriggerSchema.extend({
  referenceId: z.string().optional(),
  email: z.email({ message: Errors.EMAIL_INVALID }),
  phone: z.string().nullish(),
  shopifyCustomerId: z.string().nullish(),
}).refine(({ phone, country, locale }) => validatePhoneNumber(phone, country, locale), {
  message: Errors.INVALID_PHONE_NUMBER,
  path: ["phone"],
})

// Schema for shopifyId only
const PublicShopifyCustomerIdSchema = BaseCustomerTriggerSchema.extend({
  shopifyCustomerId: z.string(),
  locale: z.string().nullish(),
  email: z
    .email()
    .transform((val) => (val === "" ? null : val))
    .nullable()
    .refine((val) => val === null || z.email().safeParse(val).success, {
      message: Errors.EMAIL_INVALID,
    })
    .optional(),
  phone: z.string().nullish(),
})

export const AmphoraCustomerTriggerSchema = BaseCustomerTriggerSchema.extend({
  client: CompanyClientSchema,
  shopifyCustomerId: z.string().nullish(),
})

// Union of both
export const PublicCustomerTriggerSchema = z.union([
  PublicPhoneCustomerSchema,
  PublicEmailCustomerSchema,
  PublicShopifyCustomerIdSchema,
])

// JSON Schema-compatible versions (without transforms)
// Schema where phone is required, email is optional
const PublicPhoneCustomerJsonSchemaSchema = BaseCustomerTriggerSchema.extend({
  phone: z.string(),
  email: z.email().nullable().optional(),
  shopifyCustomerId: z.string().nullish(),
}).refine(({ phone, country, locale }) => validatePhoneNumber(phone, country, locale), {
  message: Errors.INVALID_PHONE_NUMBER,
  path: ["phone"],
})

// Schema where email is required, phone is optional
const PublicEmailCustomerJsonSchemaSchema = BaseCustomerTriggerSchema.extend({
  email: z.email({ message: Errors.EMAIL_INVALID }),
  phone: z.string().nullish(),
  shopifyCustomerId: z.string().nullish(),
}).refine(({ phone, country, locale }) => validatePhoneNumber(phone, country, locale), {
  message: Errors.INVALID_PHONE_NUMBER,
  path: ["phone"],
})

// Schema for shopifyId only
const PublicShopifyCustomerIdJsonSchemaSchema = BaseCustomerTriggerSchema.extend({
  shopifyCustomerId: z.string(),
  locale: z.string().nullish(),
  email: z.email().nullable().optional(),
  phone: z.string().nullish(),
})

// JSON Schema-compatible union (without transforms)
export const PublicCustomerTriggerJsonSchemaSchema = z.union([
  PublicPhoneCustomerJsonSchemaSchema,
  PublicEmailCustomerJsonSchemaSchema,
  PublicShopifyCustomerIdJsonSchemaSchema,
])

export const CustomerSearchSchema = z.object({
  conversationId: z
    .union([z.number(), z.string()])
    .transform((val) => (val ? Number(val) : val))
    .optional()
    .nullable(),
  sendTemplateConversationId: z.union([z.string(), z.number()]).transform(String).optional(),
  lastRow: z
    .preprocess((v) => (Array.isArray(v) ? v[0] : v), z.string().nullish())
    .optional()
    .catch(undefined),
  edit: z.boolean().optional(),
  noteId: z.union([z.string(), z.number()]).transform(String).optional(),
  questionId: z.union([z.string(), z.number()]).transform(String).optional(),
  issueId: z.union([z.string(), z.number()]).transform(String).optional(),
  supportTab: z
    .enum(["tickets", "questions", "newQuestions", "actionRequests", "issues"])
    .optional(),
  tab: z
    .enum(["conversations", "customer", "support", "opportunities", "notes", "developer", "issues"])
    .catch("conversations"),
})

export const DisableCustomerAIByInstagramSchema = z.object({
  ig_profile: z
    .string()
    .trim()
    .transform((value) => value.replace(/^@/, "").toLowerCase())
    .pipe(
      z
        .string()
        .min(1)
        .max(30)
        .regex(/^[a-z0-9_]+(?:\.[a-z0-9_]+)*$/),
    ),
})
